import { Box, Button, LinearProgress, Typography } from "@mui/material";
import { ArrowLeft, ImagePlus, Pencil, Save, X } from "lucide-react";
import { FileUploader } from "react-drag-drop-files";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import SuccessCaseImageModal from "../../../widgets/SuccessCaseImageModal/SuccessCaseImageModal";
import AdminPanelButton from "../../../widgets/AdminPanelButton/AdminPanelButton";
import ProfileButton from "../../../widgets/ProfileButton/ProfileButton";
import { ReactComponent as Star } from "../../../assets/kid_star.svg";
import variables from "../../../styles/variables.module.scss";
import QuestionMark from "../../../assets/question_mark.png";
import ideasServices from "../../../services/ideasServices";
import { formatSpanishDate } from "../../../utils/date";
import { ILoggedUser } from "../../../store/actions";
import CustomSnackbar from "../Snackbar/SnackBar";
import "./PanelHeader.scss";

const MENU_LABELS: Record<string, string> = {
	units: "Unidades",
	areas: "Áreas",
	categories: "Categorías",
	kpis: "KPIs",
};

interface PanelHeaderProps {
	title: string;
	menu: string;
	updateContent: (menu: string) => void;
	selectedIdea: any;
	onSave: () => void;
}

export default function PanelHeader({
	title,
	menu,
	updateContent,
	selectedIdea,
	onSave,
}: PanelHeaderProps) {
	const [isSuccesCase, setIsSuccesCase] = useState(false);
	const [isEditMode, setIsEditMode] = useState(false);
	const [successCaseIdea, setSuccessCaseIdea] = useState<any>(null);
	const [openMarkSuccessModal, setOpenMarkSuccessModal] = useState(false);
	const [openUnmarkSuccessModal, setOpenUnmarkSuccessModal] = useState(false);
	const [openAddedSnackbar, setOpenAddedSnackbar] = useState(false);
	const [openDeletedSnackbar, setOpenDeletedSnackbar] = useState(false);
	const [snackbarAddedMode, setSnackbarAddedMode] = useState("");
	const [snackbarDeletedMode, setSnackbarDeletedMode] = useState("");
	const [isImageLoading, setIsImageLoading] = useState(false);
	const [file, setFile] = useState<File>();
	const [preview, setPreview] = useState<string | null>(null);
	const [progress, setProgress] = useState<number>(50);

	useEffect(() => {
		setIsSuccesCase(selectedIdea?.is_success_case);
	}, [successCaseIdea]);

	const defaultImage = "../../../../assets/success_cases_bg.jpg";

	const loggedUser: ILoggedUser = useSelector(
		(state: any) => state.loggedUser.loggedUser,
	);

	const handleBackButton = () => {
		setIsEditMode(false);
		updateContent("ideas");
	};

	const handleCancelButton = () => {
		setIsEditMode(false);
		updateContent("idea-details");
	};

	const handleEditButton = () => {
		setIsEditMode(true);
		updateContent("edit-idea");
	};

	const handleSaveButton = () => {
		onSave();
		setIsEditMode(false);
	};

	const handleSuccessCaseIconClick = () => {
		setSuccessCaseIdea(selectedIdea);
	};

	useEffect(() => {
		if (successCaseIdea) {
			!successCaseIdea.is_success_case && setOpenMarkSuccessModal(true);
			successCaseIdea.is_success_case && setOpenUnmarkSuccessModal(true);
		}
	}, [successCaseIdea]);

	const handleChange = (file: File) => {
		setFile(file);
		const reader = new FileReader();
		setIsImageLoading(true);
		setProgress(0);

		reader.onprogress = (e) => {
			if (e.lengthComputable) {
				const percent = Math.round((e.loaded / e.total) * 100);
				setProgress(percent);
			}
		};

		reader.onloadend = () => {
			setPreview(reader.result as string);
			setIsImageLoading(false);
		};

		reader.readAsDataURL(file);
	};

	const handleMarkCancel = () => {
		setOpenMarkSuccessModal(false);
		setFile(undefined);
		setSuccessCaseIdea(null);
	};

	const handleMarkSave = async () => {
		if (file) {
			await ideasServices
				.markSuccessIdea(file, successCaseIdea.idea_id)
				.then((res) => {
					setOpenMarkSuccessModal(false);
				})
				.finally(() => setOpenAddedSnackbar(true));
		}
		setFile(undefined);
		setIsSuccesCase(true);
	};

	const handleUnmarkCancel = () => {
		setOpenUnmarkSuccessModal(false);
		setFile(undefined);
		setSuccessCaseIdea(null);
	};

	const handleUnmarkSave = async () => {
		setIsImageLoading(true);
		await ideasServices
			.unmarkSuccessIdea(successCaseIdea.idea_id)
			.then((res) => {
				setOpenUnmarkSuccessModal(false);
				setIsImageLoading(false);
			})
			.finally(() => setOpenDeletedSnackbar(true));
		setIsSuccesCase(false);
	};

	const getImageFileName = (file_route: any) => {
		if (file_route) {
			const routeParts = file_route.split("/");
			const fileName = routeParts[routeParts.length - 1];
			return file_route ? fileName : "Default";
		} else {
			return "Default";
		}
	};

	const getStatusColor = () => {
		let statusColor = "";

		switch (selectedIdea.status_id) {
			case 1:
				statusColor = variables.secondary2_300;
				break;
			case 3:
				statusColor = variables.secondary1_500;
				break;
			case 2:
				statusColor = variables.secondary2_600;
				break;
			case 4:
				statusColor = variables.primary900;
				break;
			case 5:
				statusColor = variables.primary2_900;
				break;
			case 6:
				statusColor = variables.neutral400;
				break;
		}
		return statusColor;
	};

	const getStatusFontColor = () => {
		let statusFontColor = "";

		switch (selectedIdea.status_id) {
			case 1:
				statusFontColor = variables.secondary2_900;
				break;
			case 3:
				statusFontColor = variables.secondary1_900;
				break;
			case 6:
				statusFontColor = variables.neutral700;
				break;
			default:
				statusFontColor = variables.neutral50;
		}
		return statusFontColor;
	};

	const menuMessage = () => {
		switch (menu) {
			case "units":
				return "Administra en qué hospitales las ideas estarán haciendo la diferencia.";
			case "areas":
				return "Cuando diferentes áreas se unen, las ideas se hacen más fuertes y posibles.";
			case "categories":
				return "Organizamos las ideas por categoría para enfocar esfuerzos y potenciar su impacto.";
			case "kpis":
				return "Monitoriza el uso de la plataforma para optimizar la participación y el rendimiento.";
		}
	};

	const content = () => {
		switch (title) {
			case "ideas":
			case "inicio":
				return (
					<Box className="panelHeader">
						<Box className="smallSpace" />
						<Box className="panelMessage">
							<Typography className="panelMessage__greeting">
								Hola, {loggedUser.name}
							</Typography>
							<Typography className="panelMessage__script">
								Echa un vistazo a las Ideas de Colaboradores CHRISTUS
								MUGUERZA.
							</Typography>
						</Box>
						<Box className="middleSpace" />
						<Box className="panelButtons">
							<AdminPanelButton
								text={`Salir del Panel\nAdministrador`}
								route="/"
							/>
							<ProfileButton />
						</Box>
						<Box className="smallSpace" />
					</Box>
				);
			case "units":
			case "areas":
			case "categories":
			case "kpis": {
				return (
					<Box className="panelHeader">
						<Box className="smallSpace" />
						<Box className="panelMessage">
							<Typography className="panelMessage__greeting">
								{MENU_LABELS[menu] ?? menu}
							</Typography>
							<Typography className="panelMessage__script">
								{menuMessage()}
							</Typography>
						</Box>
						<Box className="middleSpace" />
						<Box className="panelButtons">
							<AdminPanelButton
								text={`Salir del Panel\nAdministrador`}
								route="/"
							/>
							<ProfileButton />
						</Box>
						<Box className="smallSpace" />
					</Box>
				);
			}
			case "edit-idea":
			case "idea-details": {
				return (
					<Box className="panelHeader--higher">
						<Button
							onClick={handleBackButton}
							className="panelHeader--higher__back-btn"
							disableRipple
						>
							<ArrowLeft />
							REGRESAR
						</Button>
						<Box className="panelHeader--higher__content">
							<Box
								className="panelHeader--higher__content__text"
								sx={{ opacity: isEditMode ? 0.5 : 1 }}
							>
								{!isEditMode && (
									<Typography
										className="panelHeader--higher__content__text__status"
										sx={{
											backgroundColor: getStatusColor(),
											color: getStatusFontColor(),
										}}
									>
										{selectedIdea.status
											.toLowerCase()
											.split(" ")
											.map(
												(word: string) =>
													word.charAt(0).toUpperCase() +
													word.slice(1),
											)
											.join(" ")}
									</Typography>
								)}
								<Typography className="panelHeader--higher__content__text__title">
									{selectedIdea.title}
								</Typography>
								<Typography className="panelHeader--higher__content__text__area">
									{selectedIdea.hospitalAreas}
								</Typography>
								<Typography className="panelHeader--higher__content__text__date">
									{formatSpanishDate(selectedIdea.created_at)}
								</Typography>
							</Box>
							<Box className="panelHeader--higher__content__buttons">
								{isEditMode ? (
									<Button onClick={handleCancelButton}>
										<X
											className="panelHeader--higher__content__buttons__icon"
											style={{ marginBottom: "2px" }}
										/>
										Cancelar
									</Button>
								) : isSuccesCase ? (
									<Button onClick={handleSuccessCaseIconClick}>
										<Star className="panelHeader--higher__content__buttons__star--filled" />
										Caso de éxito
									</Button>
								) : (
									<Button onClick={handleSuccessCaseIconClick}>
										<Star className="panelHeader--higher__content__buttons__star" />
										Marcar como Caso de éxito
									</Button>
								)}
								{isEditMode ? (
									<Button
										className="panelHeader--higher__content__buttons--save-btn"
										onClick={handleSaveButton}
									>
										<Save className="panelHeader--higher__content__buttons__icon--save" />{" "}
										Guardar
									</Button>
								) : (
									<Button onClick={handleEditButton}>
										<Pencil className="panelHeader--higher__content__buttons__icon" />{" "}
										Editar
									</Button>
								)}
							</Box>
						</Box>
					</Box>
				);
			}
		}
	};

	return (
		<>
			{content()}
			<SuccessCaseImageModal
				openSuccessModal={openMarkSuccessModal}
				setOpenSuccessModal={setOpenMarkSuccessModal}
				title="¿Esta idea fue un éxito?"
				subtitle="Adjunta una imagen para mostrar esta idea en la sección de Casos de éxito."
				content={
					!isImageLoading && !file ? (
						<FileUploader
							handleChange={handleChange}
							multiple={false}
						>
							<Box className="sc-image-modal__content__image-container">
								<ImagePlus size={50} />
								<Typography>
									Arrastra una imagen aquí o haz click para adjuntar un
									archivo.
									<br />
									(Dimensiones recomendadas: 1920x444px)
								</Typography>
							</Box>
						</FileUploader>
					) : (
						<Box className="sc-image-modal__content__image-loading">
							<img
								className="sc-image-modal__content__image-loading__image"
								src={preview ?? QuestionMark}
								alt=""
							/>
							<Box className="sc-image-modal__content__image-loading__info">
								<Typography className="sc-image-modal__content__image-loading__info-name">
									{file?.name ?? "procesoadmision.png"}
								</Typography>
								<Typography className="sc-image-modal__content__image-loading__info-size">
									{file?.size ?? "1.2 MB"}
								</Typography>
								<Box sx={{ display: "flex", flexDirection: "row" }}>
									<LinearProgress
										className="sc-image-modal__content__image-loading__info-bar"
										variant="determinate"
										value={progress}
									/>
									<Typography className="sc-image-modal__content__image-loading__info-percentage">
										{progress}%
									</Typography>
								</Box>
							</Box>
							<X
								className="sc-image-modal__content__image-loading__x"
								onClick={() => {
									setFile(undefined);
								}}
							/>
						</Box>
					)
				}
				nextButton="Guardar"
				disableButton={!file}
				save={handleMarkSave}
				cancel={handleMarkCancel}
			/>

			<SuccessCaseImageModal
				openSuccessModal={openUnmarkSuccessModal}
				setOpenSuccessModal={setOpenUnmarkSuccessModal}
				title="¿Quitar de los casos de éxito?"
				subtitle="Se eliminará también la imagen adjunta."
				content={
					!isImageLoading ? (
						<Box className="sc-image-modal__content__image-container">
							<img
								className="sc-image-modal__content__image-container__image-thumbnail"
								src={
									successCaseIdea
										? successCaseIdea.image_url
										: defaultImage
								}
								alt=""
							/>
							<Box className="sc-image-modal__content__image-container__image-name">
								{successCaseIdea
									? getImageFileName(successCaseIdea.image_url)
									: "Default Text"}
							</Box>
						</Box>
					) : (
						<Box className="sc-image-modal__content__image-loading">
							<img
								className="sc-image-modal__content__image-loading__image"
								src={preview ?? QuestionMark}
								alt=""
							/>
							<Box className="sc-image-modal__content__image-loading__info">
								<Typography className="sc-image-modal__content__image-loading__info-name">
									{file?.name ?? "procesoadmision.png"}
								</Typography>
								<Typography className="sc-image-modal__content__image-loading__info-size">
									{file?.size ?? "1.2 MB"}
								</Typography>
								<Box sx={{ display: "flex", flexDirection: "row" }}>
									<LinearProgress
										className="sc-image-modal__content__image-loading__info-bar"
										variant="determinate"
										value={progress}
									/>
									<Typography className="sc-image-modal__content__image-loading__info-percentage">
										{progress}%
									</Typography>
								</Box>
							</Box>
						</Box>
					)
				}
				nextButton="Quitar"
				save={handleUnmarkSave}
				cancel={handleUnmarkCancel}
			/>

			<CustomSnackbar
				open={openAddedSnackbar}
				onClose={() => setOpenAddedSnackbar(false)}
				menu="added-success-case"
				mode={snackbarAddedMode}
			/>

			<CustomSnackbar
				open={openDeletedSnackbar}
				onClose={() => setOpenDeletedSnackbar(false)}
				menu="deleted-success-case"
				mode={snackbarDeletedMode}
			/>
		</>
	);
}
